import { Heading } from '@mm/components';
import { CloseIcon, PlusCircleIcon } from '@mm/icons';
import { colors } from '@mm/utils';
import { StoreState } from '@mm/state/store-interfaces';
import ConnectionModel from '@mm/state/model/ConnectionModel';
import { useViewActions } from '@mm/state/actions/viewActions';
import { EditIcon } from '@mm/icons/AntDesignIcons';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import styles from './ConnectionsSlideoutEditor.module.css';
import PrimarySelect from '../../../../../common/components/PrimarySelect/PrimarySelect';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { SendDataConfig } from '@mm/state/model/WidgetModel';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { PlusOutlined } from '@ant-design/icons';

export type ConnectionsSlideoutProps = {
  widgetId: number;
  connections: ConnectionModel[];
  onConnectionChange: (connection: ConnectionModel) => void;
  onEditConnection: (selectedConnectionId: number) => void;
  onAddNewConnection: () => void;
  title: string;
  selector: any;
  type?: 'mailchimp' | 'google_spreadsheets' | 'facebook_ads_audience';
};

export const ConnectionsSlideoutEditor = (props: ConnectionsSlideoutProps) => {
  const {
    widgetId,
    onConnectionChange,
    onEditConnection,
    onAddNewConnection,
    title,
    selector,
    type,
  } = props;
  const viewActions = useViewActions();
  const { setRef } = useSlideoutOnClose();
  const { retrieveWidgetConnections } = useConnectionCRUDActions();
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const widgets = useSelector((state: StoreState) => state.widget.entities);
  const widget = widgets.find(w => w.id === widgetId)!;
  const { connectionWebhookId } = widget.config as Partial<SendDataConfig>;
  const connections: ConnectionModel[] = useSelector(selector);
  const selectedConnection =
    connections && connections.find(c => c.id === connectionWebhookId);

  useEffect(() => {
    retrieveWidgetConnections(activeFbPageId, type);
  }, []);

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <div className={styles.headerContent}>
        <Heading className={styles.headerTitle} size={3}>
          {title}
        </Heading>
        <div className={styles.headerButtons}>
          <div
            onClick={viewActions.hideSlideoutMenu}
            className={styles.closeIcon}
          >
            <CloseIcon color="#8c8c8c" />
          </div>
        </div>
      </div>
      <SlideoutMenu.Content className={styles.content}>
        <div className={styles.configArea}>
          <div className={styles.chooseConnection}>
            <div className={styles.header}>
              Choose Connection:
              <PrimaryButton
                color={ButtonColor.transparent}
                leftIcon={<PlusOutlined color={colors.accentBlue} />}
                onClick={onAddNewConnection}
                className={styles.primaryButton}
              >
                New Connection
              </PrimaryButton>
            </div>
            <div className={styles.connectionPanel}>
              <PrimarySelect
                placeholder="Choose"
                className={styles.connectionSelect}
                onChange={onConnectionChange}
                value={selectedConnection}
                options={connections}
                showSearchFilter={true}
                closeOnSearchRightSideClick={true}
                customDropdownStyles={{
                  zIndex: '1000003',
                }}
              />
            </div>
            {selectedConnection && (
              <PrimaryButton
                color={ButtonColor.gray}
                leftIcon={
                  <EditIcon color="#262626" className={styles.editIcon} />
                }
                className={styles.editBtn}
                onClick={() => onEditConnection(selectedConnection.id)}
              >
                Edit Connection
              </PrimaryButton>
            )}
          </div>
        </div>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
